import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useAccountStore, StoredAccount } from '@/stores/useAccountStore';
import { useLanguage } from '@/contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { eventService } from '@/services/eventService';
import { BASE_URL } from '@/config/api';
import { getDeviceInfo } from '@/utils/device-detection';
import { getIpInfo } from '@/utils/ip-detection';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import SocialAuthButtons from '@/components/auth/SocialAuthButtons';
import { useToast } from '@/hooks/use-toast';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { User, LogOut, Plus, ChevronDown, Check, Building, Loader2, X, HelpCircle, ShieldCheck, MessagesSquare } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AccountSwitcherProps {
  isMobile?: boolean;
  onMenuClose?: () => void;
}

export function AccountSwitcher({ isMobile = false, onMenuClose }: AccountSwitcherProps) {
  const { user, token, logout } = useAuth();
  const { accounts, removeAccount, addAccount, syncCurrentUser } = useAccountStore();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [showAddModal, setShowAddModal] = useState(false);
  const [isLogging, setIsLogging] = useState(false);
  const [loginUsername, setLoginUsername] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  // 2FA state for the "add another account" flow
  const [otpStep, setOtpStep] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState<string | null>(null);
  const [pendingProvider, setPendingProvider] = useState<'email' | 'google' | 'apple'>('email');
  const [pendingSocialToken, setPendingSocialToken] = useState<string | null>(null);

  const currentUserId = user?.id || user?.userId;

  // Filter out current account from the list
  const otherAccounts = accounts.filter(a => a.id !== currentUserId);

  // Determine if the user is a student
  const isStudent = user?.account_type === "Student";
  const isParent = user?.account_type === "Parent";
  const isSchool = user?.account_type === "S";
  const isCoach = user?.account_type === "C";

  const getInitials = (firstname?: string, lastname?: string) => {
    return `${(firstname || '')[0] || ''}${(lastname || '')[0] || ''}`.toUpperCase() || '?';
  };

  const getAccountLabel = (account: StoredAccount) => {
    if (account.account_type === 'S' && account.roleType === 'school_admin') {
      return account.school_name || 'School';
    }
    return `${account.firstname} ${account.lastname}`.trim();
  };

  const getAccountSublabel = (account: StoredAccount) => {
    const typeMap: Record<string, string> = {
      'C': 'Coach',
      'S': 'School',
      'Student': 'Student',
      'Parent': 'Parent',
    };
    return typeMap[account.account_type] || account.email;
  };

  const handleSwitchAccount = async (account: StoredAccount) => {
    // Save current account first
    if (user && token) {
      syncCurrentUser(user, token);
    }

    // Switch: set the new token and reload
    localStorage.setItem('auth-token', account.token);

    //reset localstorage INVOICES_STORAGE_KEY invoices-pagination-params
    localStorage.removeItem('invoices-pagination-params');
    
    // Clear caches
    eventService.clearEventsCache();
    queryClient.clear();
    
    // Reload the page to re-init auth with new token
    window.location.href = '/agenda';
  };

  const resetAddModal = () => {
    setLoginUsername('');
    setLoginPassword('');
    setOtpStep(false);
    setOtpCode('');
    setOtpError(null);
    setPendingProvider('email');
    setPendingSocialToken(null);
  };

  const finalizeNewAccount = (data: any) => {
    const newAccount: StoredAccount = {
      id: data.user.id || data.user.userId,
      token: data.access_token,
      firstname: data.user.firstname,
      lastname: data.user.lastname,
      email: data.user.email,
      account_type: data.user.account_type,
      image_url: data.user.image_url,
      school_name: data.user.school_name,
      roleType: data.user.roleType,
    };
    addAccount(newAccount);
    localStorage.removeItem('invoices-pagination-params');

    setShowAddModal(false);
    resetAddModal();

    toast({
      title: t('login.loginSuccess'),
      description: `${newAccount.firstname} ${newAccount.lastname}`,
    });

    // Switch to the new account
    localStorage.setItem('auth-token', data.access_token);
    eventService.clearEventsCache();
    queryClient.clear();
    window.location.href = '/agenda';
  };

  /**
   * Performs the login request for the account being added.
   * Returns 'success' | '2fa_required' | 'error' — same semantics as the login page.
   */
  const attemptAddLogin = async (
    provider: 'email' | 'google' | 'apple',
    payload: { username?: string; password?: string; socialToken?: string },
    code?: string,
  ): Promise<'success' | '2fa_required' | 'error'> => {
    // Save current user before switching
    if (user && token) {
      syncCurrentUser(user, token);
    }

    const ipInfo = await getIpInfo();
    const deviceInfo = getDeviceInfo();
    const common = {
      ip_address: ipInfo.ip,
      country: ipInfo.countryCode,
      device_info: JSON.stringify(deviceInfo),
      ...(code ? { code } : {}),
    };

    const url =
      provider === 'google'
        ? `${BASE_URL}/api/auth/google`
        : provider === 'apple'
        ? `${BASE_URL}/api/auth/apple`
        : `${BASE_URL}/api/login`;

    const body =
      provider === 'email'
        ? { username: payload.username, password: payload.password, ...common }
        : { token: payload.socialToken, ...common };

    const response = await fetch(url, {
      method: 'POST',
      ...(provider === 'email' ? { credentials: 'include' as RequestCredentials } : {}),
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    });

    let data: any = {};
    try {
      data = await response.json();
    } catch {
      data = {};
    }

    const message = typeof data?.message === 'string' ? data.message : '';
    const requiresTwoFactor =
      data?.two_factor_required === true ||
      message === 'two_factor_required' ||
      message.includes('two_factor');

    if (requiresTwoFactor) {
      setPendingProvider(provider);
      setPendingSocialToken(payload.socialToken ?? null);
      return '2fa_required';
    }

    const succeeded =
      response.ok && !!data?.access_token && (provider !== 'email' || message === 'success');

    if (succeeded) {
      finalizeNewAccount(data);
      return 'success';
    }

    return 'error';
  };

  const handleAddAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLogging(true);
    try {
      const result = await attemptAddLogin('email', {
        username: loginUsername,
        password: loginPassword,
      });
      if (result === '2fa_required') {
        setOtpCode('');
        setOtpError(null);
        setOtpStep(true);
      } else if (result === 'error') {
        toast({
          variant: 'destructive',
          title: t('login.loginFailed'),
          description: t('login.pleaseTryAgain'),
        });
      }
    } catch (error) {
      console.error('Add account error:', error);
      toast({
        variant: 'destructive',
        title: t('login.loginFailed'),
        description: t('login.pleaseTryAgain'),
      });
    } finally {
      setIsLogging(false);
    }
  };

  const handleSocialCredential = async (provider: 'google' | 'apple', idToken: string) => {
    setIsLogging(true);
    try {
      const result = await attemptAddLogin(provider, { socialToken: idToken });
      if (result === '2fa_required') {
        setOtpCode('');
        setOtpError(null);
        setOtpStep(true);
      } else if (result === 'error') {
        toast({
          variant: 'destructive',
          title: t('login.loginFailed'),
          description: t('login.pleaseTryAgain'),
        });
      }
    } catch (error) {
      console.error('Add account social error:', error);
      toast({
        variant: 'destructive',
        title: t('login.loginFailed'),
        description: t('login.pleaseTryAgain'),
      });
    } finally {
      setIsLogging(false);
    }
  };

  const handleAddOtpSubmit = async (code: string) => {
    setIsLogging(true);
    setOtpError(null);
    try {
      const result = await attemptAddLogin(
        pendingProvider,
        pendingProvider === 'email'
          ? { username: loginUsername, password: loginPassword }
          : { socialToken: pendingSocialToken || undefined },
        code,
      );
      if (result !== 'success') {
        setOtpCode('');
        setOtpError(t('login.invalidCode') || 'Code invalide, veuillez réessayer.');
      }
    } catch {
      setOtpCode('');
      setOtpError(t('login.invalidCode') || 'Code invalide, veuillez réessayer.');
    } finally {
      setIsLogging(false);
    }
  };

  /** Shared modal body: credentials + social buttons, or the 2FA code step. */
  const addAccountBody = (idSuffix: string) => {
    if (otpStep) {
      return (
        <div className="flex flex-col items-center gap-4 pt-2">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
            <ShieldCheck className="h-7 w-7 text-primary" />
          </div>
          <p className="text-center text-sm text-muted-foreground">{t('login.twoFaSubtitle')}</p>
          <InputOTP
            maxLength={6}
            value={otpCode}
            disabled={isLogging}
            autoFocus
            onChange={(((v: string) => {
              setOtpCode(v);
              if (v.length === 6) handleAddOtpSubmit(v);
            }) as never)}
          >
            <InputOTPGroup className="gap-2">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <InputOTPSlot
                  key={i}
                  index={i}
                  className="h-14 w-11 text-2xl font-bold rounded-lg border-2 first:rounded-l-lg last:rounded-r-lg"
                />
              ))}
            </InputOTPGroup>
          </InputOTP>
          {otpError && <p className="text-sm text-destructive">{otpError}</p>}
          <Button
            className="w-full h-11"
            disabled={isLogging || otpCode.length !== 6}
            onClick={() => handleAddOtpSubmit(otpCode)}
          >
            {isLogging && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {t('login.verif')}
          </Button>
          <Button
            variant="ghost"
            className="w-full"
            disabled={isLogging}
            onClick={() => {
              setOtpStep(false);
              setOtpCode('');
              setOtpError(null);
            }}
          >
            {t('common.cancel')}
          </Button>
        </div>
      );
    }

    return (
      <>
        <form onSubmit={handleAddAccount} className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label htmlFor={`switch-username-${idSuffix}`}>{t('login.username')}</Label>
            <Input
              id={`switch-username-${idSuffix}`}
              value={loginUsername}
              onChange={(e) => setLoginUsername(e.target.value)}
              placeholder={t('login.usernamePlaceholder')}
              required
              autoComplete="username"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`switch-password-${idSuffix}`}>{t('login.password')}</Label>
            <Input
              id={`switch-password-${idSuffix}`}
              type="password"
              value={loginPassword}
              onChange={(e) => setLoginPassword(e.target.value)}
              placeholder="••••••••"
              required
              autoComplete="current-password"
            />
          </div>
          <Button type="submit" className="w-full" disabled={isLogging}>
            {isLogging ? (
              <>
                <Loader2 size={16} className="mr-2 animate-spin" />
                {t('common.loading')}
              </>
            ) : (
              t('common.login')
            )}
          </Button>
        </form>

        <div className="my-4 relative flex items-center justify-center">
          <Separator className="w-full" />
          <span className="absolute bg-background px-3 text-xs uppercase tracking-wide text-muted-foreground">
            {t('login.orContinueWith')}
          </span>
        </div>

        <SocialAuthButtons appStyle onCredential={handleSocialCredential} />
      </>
    );
  };

  const handleRemoveAccount = (e: React.MouseEvent, accountId: string) => {
    e.stopPropagation();
    removeAccount(accountId);
    toast({ title: 'Account removed' });
  };

  // Mobile version
  if (isMobile) {
    return (
      <>
        {/* Current account section */}
        <div className="px-4 py-3 bg-muted/50 rounded-lg mb-2">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10">
              <AvatarImage src={user?.image_url} />
              <AvatarFallback className="bg-primary/10 text-primary text-sm font-semibold">
                {isSchool && user?.roleType === 'school_admin' ? (
                  <Building size={18} />
                ) : (
                  getInitials(user?.firstname, user?.lastname)
                )}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col min-w-0 flex-1">
              <span className="text-sm font-semibold truncate">
                {isSchool && user?.roleType === 'school_admin' 
                  ? (user?.school_name || 'School') 
                  : `${user?.firstname} ${user?.lastname}`}
              </span>
              <span className="text-xs text-muted-foreground truncate">{user?.email}</span>
            </div>
            <div className="flex items-center justify-center h-6 w-6 bg-primary/10 rounded-full">
              <Check size={14} className="text-primary" />
            </div>
          </div>
        </div>

        {/* Other accounts */}
        {otherAccounts.length > 0 && (
          <>
            <p className="text-xs text-muted-foreground font-medium px-4 py-2">
              {t('login.switchAccount')}
            </p>
            {otherAccounts.map((account, index) => (
              <div
                key={account.id || index}
                className="w-full px-4 py-3 hover:bg-secondary rounded-md flex items-center gap-3 text-left"
                onClick={() => {
                  handleSwitchAccount(account);
                  onMenuClose?.();
                }}
              >
                <Avatar className="h-8 w-8">
                  <AvatarImage src={account.image_url} />
                  <AvatarFallback className="bg-muted text-muted-foreground text-xs font-medium">
                    {account.account_type === 'S' ? (
                      <Building size={14} />
                    ) : (
                      getInitials(account.firstname, account.lastname)
                    )}
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col min-w-0 flex-1">
                  <span className="text-sm font-medium truncate">{getAccountLabel(account)}</span>
                  <span className="text-xs text-muted-foreground">{getAccountSublabel(account)}</span>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveAccount(e, account.id);
                  }}
                  className="shrink-0 p-1.5 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </>
        )}

        {/* Add account */}
        <button
          className="w-full px-4 py-3 hover:bg-secondary rounded-md flex items-center gap-3 text-left"
          onClick={() => {
            setShowAddModal(true);
            onMenuClose?.();
          }}
        >
          <div className="h-8 w-8 rounded-full border-2 border-dashed border-muted-foreground/30 flex items-center justify-center">
            <Plus size={14} className="text-muted-foreground" />
          </div>
          <span className="text-sm font-medium">{t('login.addAnotherAccount')}</span>
        </button>

        {/* Add Account Modal */}
        <Dialog open={showAddModal} onOpenChange={(v) => { setShowAddModal(v); if (!v) resetAddModal(); }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {otpStep ? <ShieldCheck size={20} /> : <Plus size={20} />}
                {otpStep ? t('login.twoFaTitle') : t('login.addAnotherAccount')}
              </DialogTitle>
              {!otpStep && (
                <DialogDescription>
                  {t('login.addAnotherAccountDesc')}.
                </DialogDescription>
              )}
            </DialogHeader>
            {addAccountBody('mobile')}
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="flex items-center gap-2 max-w-[180px]">
            <Avatar className="h-6 w-6">
              <AvatarImage src={user?.image_url} />
              <AvatarFallback className="text-[10px] bg-primary/10 text-primary font-semibold">
                {isSchool && user?.roleType === 'school_admin' ? (
                  <Building size={14} />
                ) : (
                  getInitials(user?.firstname, user?.lastname)
                )}
              </AvatarFallback>
            </Avatar>
            <span className="truncate text-sm font-medium hidden sm:inline">
              {isSchool && user?.roleType === 'school_admin' ? 'School' : user?.firstname}
            </span>
            <ChevronDown size={14} className="shrink-0 opacity-50" />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-72">
          {/* Current account */}
          <DropdownMenuLabel className="font-normal">
            <div className="flex items-center gap-3 py-1">
              <Avatar className="h-9 w-9">
                <AvatarImage src={user?.image_url} />
                <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
                  {isSchool && user?.roleType === 'school_admin' ? (
                    <Building size={16} />
                  ) : (
                    getInitials(user?.firstname, user?.lastname)
                  )}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col min-w-0">
                <span className="text-sm font-semibold truncate">
                  {isSchool && user?.roleType === 'school_admin' 
                    ? (user?.school_name || 'School') 
                    : `${user?.firstname} ${user?.lastname}`}
                </span>
                <span className="text-xs text-muted-foreground truncate">{user?.email}</span>
              </div>
              <Check size={16} className="shrink-0 text-primary ml-auto" />
            </div>
          </DropdownMenuLabel>


             <DropdownMenuSeparator />

           {/* My Account link */}
          <DropdownMenuItem
            className="cursor-pointer py-2.5 px-2"
            onClick={() => navigate('/my-account')}
          >
            <User size={16} className="mr-2" />
            <span className="text-sm">{t('nav.myAccount')}</span>
          </DropdownMenuItem>

           <DropdownMenuItem
            className="cursor-pointer py-2.5 px-2"
            onClick={() => navigate('/messages')}
          >
            <MessagesSquare size={16} className="mr-2" />
            <span className="text-sm">{t('nav.messagerie')}</span>
          </DropdownMenuItem>

          {!isStudent && !isParent && (
           <DropdownMenuItem
            className="cursor-pointer py-2.5 px-2"
            onClick={() => navigate('/contact-support')}
          >
            <HelpCircle size={16} className="mr-2" />
            <span className="text-sm">{t('nav.support')}</span>
          </DropdownMenuItem>
          )}

       

          {/* Other accounts */}
          {otherAccounts.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs text-muted-foreground font-medium px-2 py-1.5">
                {t('login.switchAccount')}
              </DropdownMenuLabel>
              {otherAccounts.map((account, index) => (
                <DropdownMenuItem
                  key={account.id || index}
                  className="cursor-pointer py-2.5 px-2"
                  onClick={() => handleSwitchAccount(account)}
                >
                  <div className="flex items-center gap-3 w-full">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={account.image_url} />
                      <AvatarFallback className="bg-muted text-muted-foreground text-xs font-medium">
                        {account.account_type === 'S' ? (
                          <Building size={14} />
                        ) : (
                          getInitials(account.firstname, account.lastname)
                        )}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="text-sm font-medium truncate">{getAccountLabel(account)}</span>
                      <span className="text-xs text-muted-foreground">{getAccountSublabel(account)}</span>
                    </div>
                    <button
                      onClick={(e) => handleRemoveAccount(e, account.id)}
                      className="shrink-0 p-1 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                    >
                      <X size={14} />
                    </button>
                  </div>
                </DropdownMenuItem>
              ))}
            </>
          )}

          <DropdownMenuSeparator />

          {/* Add account */}
          <DropdownMenuItem
            className="cursor-pointer py-2.5 px-2"
            onClick={() => setShowAddModal(true)}
          >
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-full border-2 border-dashed border-muted-foreground/30 flex items-center justify-center">
                <Plus size={14} className="text-muted-foreground" />
              </div>
              <span className="text-sm font-medium">{t('login.addAnotherAccount')}</span>
            </div>
          </DropdownMenuItem>

          <DropdownMenuSeparator />

         


          {/* Logout */}
          <DropdownMenuItem
            className="cursor-pointer py-2.5 px-2 text-destructive focus:text-destructive"
            onClick={logout}
          >
            <LogOut size={16} className="mr-2" />
            <span className="text-sm">{t('login.logout')}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Add Account Modal */}
      <Dialog open={showAddModal} onOpenChange={(v) => { setShowAddModal(v); if (!v) resetAddModal(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {otpStep ? <ShieldCheck size={20} /> : <Plus size={20} />}
              {otpStep ? t('login.twoFaTitle') : t('login.addAnotherAccount')}
            </DialogTitle>
            {!otpStep && (
              <DialogDescription>
                {t('login.addAnotherAccountDesc')}.
              </DialogDescription>
            )}
          </DialogHeader>
          {addAccountBody('desktop')}
        </DialogContent>
      </Dialog>
    </>
  );
}
